import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Users, Upload, Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

function parseCSV(text) {
  const rows = [];
  let cur = [], field = "", inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else inQuotes = false;
      } else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ",") { cur.push(field); field = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      cur.push(field); field = "";
      if (cur.some((f) => f.trim() !== "")) rows.push(cur);
      cur = [];
    } else field += c;
  }
  cur.push(field);
  if (cur.some((f) => f.trim() !== "")) rows.push(cur);
  if (rows.length < 2) return [];
  const headers = rows[0].map((h) => h.trim().toLowerCase().replace(/\s+/g, "_"));
  return rows.slice(1).map((r) => Object.fromEntries(headers.map((h, ix) => [h, (r[ix] || "").trim()])));
}

const KIND_OPTIONS = [
  { value: "CUSTOMER", label: "Customer" },
  { value: "BUILDER", label: "Builder" },
  { value: "ARCHITECT", label: "Architect" },
  { value: "DESIGNER", label: "Designer" },
  { value: "CONTRACTOR", label: "Contractor" },
  { value: "END_USER", label: "End user" },
];
const CREDIT_TERMS = ["COD", "NET 15", "NET 30", "NET 45", "NET 60", "NET 90"];

const EMPTY = {
  name: "",
  kind: "CUSTOMER",
  phone: "",
  email: "",
  gst: "",
  address: "",
  region: "",
  credit_limit: 0,
  credit_terms: "NET 30",
  notes: "",
};

/** Reusable Customers management page — used by Vendor + Distributor + Org portals. */
export default function CustomersManage({ title = "Customers", subtitle = "Manage buyers, builders, architects, designers, contractors and end-users." }) {
  const [rows, setRows] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkFileName, setBulkFileName] = useState("");
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/sales/customers");
      setRows(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load customers");
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Customer name is required");
    setSaving(true);
    try {
      await api.post("/sales/customers", {
        ...form,
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        gst: form.gst.trim() || null,
        address: form.address.trim() || null,
        region: form.region.trim() || null,
        credit_limit: Number(form.credit_limit) || 0,
        credit_terms: form.credit_terms || null,
        notes: form.notes.trim() || null,
      });
      toast.success(`${form.name.trim()} added`);
      setAddOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add customer");
    } finally {
      setSaving(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/sales/customers/template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "customers-template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to download template");
    }
  };

  const onBulkFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkFileName(file.name);
    setBulkResult(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = parseCSV(String(ev.target?.result || ""));
        setBulkRows(parsed);
        if (parsed.length === 0) toast.error("No rows found in CSV");
      } catch (err) {
        toast.error("Failed to parse CSV");
      }
    };
    reader.readAsText(file);
  };

  const submitBulk = async () => {
    if (bulkRows.length === 0) return toast.error("Upload a CSV first");
    setBulkUploading(true);
    try {
      const { data } = await api.post("/sales/customers/bulk", { customers: bulkRows });
      setBulkResult(data);
      toast.success(`Imported ${data.created} customer(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to import");
    } finally {
      setBulkUploading(false);
    }
  };

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.name || "").toLowerCase().includes(s)
      || (r.email || "").toLowerCase().includes(s)
      || (r.phone || "").toLowerCase().includes(s)
      || (r.region || "").toLowerCase().includes(s);
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="customers-manage">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-2"><Users className="w-7 h-7 text-blue-600" /> {title}</h1>
          <p className="text-muted-foreground">{subtitle}</p>
        </div>
        <div className="flex gap-2 items-center">
          <Input placeholder="Search name, email, region…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-64" data-testid="customers-search" />
          <Button variant="outline" onClick={() => { setBulkRows([]); setBulkFileName(""); setBulkResult(null); setBulkOpen(true); }} data-testid="customers-bulk-btn">
            <Upload className="w-4 h-4 mr-1" /> Bulk upload
          </Button>
          <Button onClick={() => { setForm(EMPTY); setAddOpen(true); }} data-testid="customers-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Add customer
          </Button>
        </div>
      </div>

      <Card><CardContent className="p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Region</TableHead>
            <TableHead>Contact</TableHead>
            <TableHead>GST</TableHead>
            <TableHead>Credit terms</TableHead>
            <TableHead className="text-right">Credit limit (₹)</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((r) => (
              <TableRow key={r.id} data-testid={`customer-row-${r.id}`}>
                <TableCell>
                  <div className="font-semibold">{r.name}</div>
                  {r.notes ? <div className="text-[10px] text-muted-foreground truncate max-w-[240px]">{r.notes}</div> : null}
                </TableCell>
                <TableCell><Badge variant="outline" className="font-mono text-[10px]">{r.kind || "CUSTOMER"}</Badge></TableCell>
                <TableCell>{r.region || "—"}</TableCell>
                <TableCell>
                  <div className="text-xs">{r.phone || "—"}</div>
                  <div className="text-[10px] text-muted-foreground">{r.email || ""}</div>
                </TableCell>
                <TableCell className="font-mono text-xs">{r.gst || "—"}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px]">{r.credit_terms || "—"}</Badge></TableCell>
                <TableCell className="text-right font-mono">{Number(r.credit_limit || 0).toLocaleString()}</TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No customers yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-2xl" data-testid="customers-add-dialog">
          <DialogHeader><DialogTitle>Add customer</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label htmlFor="cust-name">Name *</Label>
              <Input id="cust-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cust-name" placeholder="Acme Ceramics" />
            </div>
            <div>
              <Label>Company type</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="cust-kind"><SelectValue /></SelectTrigger>
                <SelectContent>{KIND_OPTIONS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="cust-region">Region</Label>
              <Input id="cust-region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} data-testid="cust-region" placeholder="Gujarat" />
            </div>
            <div>
              <Label htmlFor="cust-phone">Phone</Label>
              <Input id="cust-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cust-phone" placeholder="+91-98xxxxxxxx" />
            </div>
            <div>
              <Label htmlFor="cust-email">Email</Label>
              <Input id="cust-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cust-email" placeholder="buyer@example.com" />
            </div>
            <div>
              <Label htmlFor="cust-gst">GST number</Label>
              <Input id="cust-gst" value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} data-testid="cust-gst" placeholder="29ABCDE1234F1Z5" />
            </div>
            <div>
              <Label>Credit terms</Label>
              <Select value={form.credit_terms} onValueChange={(v) => setForm({ ...form, credit_terms: v })}>
                <SelectTrigger data-testid="cust-terms"><SelectValue /></SelectTrigger>
                <SelectContent>{CREDIT_TERMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="cust-limit">Credit limit (₹)</Label>
              <Input id="cust-limit" type="number" min="0" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} data-testid="cust-limit" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="cust-address">Address</Label>
              <Input id="cust-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="cust-address" placeholder="Street, City, State — PIN" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="cust-notes">Notes</Label>
              <Input id="cust-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cust-notes" placeholder="Any special terms, contact preference, etc." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} data-testid="cust-cancel">Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="cust-submit">{saving ? "Saving…" : "Add customer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-2xl" data-testid="customers-bulk-dialog">
          <DialogHeader><DialogTitle>Bulk upload customers (CSV)</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="customers-bulk-template-btn">
                <Download className="w-4 h-4 mr-1" /> Download CSV template
              </Button>
              <span className="text-xs text-muted-foreground">Columns: name*, kind, phone, email, gst, address, region, credit_limit, credit_terms, notes</span>
            </div>
            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-xl p-6 cursor-pointer hover:bg-muted/40 transition-colors" data-testid="customers-bulk-dropzone">
              <FileSpreadsheet className="w-8 h-8 text-muted-foreground" />
              <span className="text-sm font-medium">{bulkFileName || "Click to choose a .csv file"}</span>
              <span className="text-xs text-muted-foreground">Rows with an existing email are skipped automatically</span>
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={onBulkFile} data-testid="customers-bulk-file-input" />
            </label>
            {bulkRows.length > 0 && (
              <div className="space-y-2">
                <div className="text-sm font-semibold" data-testid="customers-bulk-preview-count">{bulkRows.length} row(s) parsed — preview (first 5):</div>
                <div className="overflow-x-auto border border-border rounded-lg">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Region</TableHead><TableHead className="text-right">Credit limit</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {bulkRows.slice(0, 5).map((r, ix) => (
                        <TableRow key={ix}>
                          <TableCell className="text-sm font-medium">{r.name || <span className="text-red-500 text-xs">missing</span>}</TableCell>
                          <TableCell className="text-xs">{r.kind || "CUSTOMER"}</TableCell>
                          <TableCell className="text-xs">{r.email || "—"}</TableCell>
                          <TableCell className="text-xs">{r.phone || "—"}</TableCell>
                          <TableCell className="text-xs">{r.region || "—"}</TableCell>
                          <TableCell className="text-right font-mono text-xs">{r.credit_limit || "0"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
            {bulkResult && (
              <div className="text-sm rounded-lg border border-border bg-muted/40 p-3" data-testid="customers-bulk-result">
                <span className="font-semibold text-emerald-600">{bulkResult.created} created</span>
                {bulkResult.skipped_existing > 0 && <span className="text-muted-foreground"> · {bulkResult.skipped_existing} skipped (already exist)</span>}
                {(bulkResult.errors || []).length > 0 && (
                  <div className="text-xs text-red-500 mt-1">{bulkResult.errors.map((e) => `Row ${e.row}: ${e.error}`).join("; ")}</div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} data-testid="customers-bulk-close">Close</Button>
            <Button onClick={submitBulk} disabled={bulkUploading || bulkRows.length === 0} data-testid="customers-bulk-submit">
              {bulkUploading ? "Uploading…" : `Import ${bulkRows.length || ""} customer(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
